import React, { useState, useEffect, useCallback } from 'react';
import { formatCurrency, formatNumber } from '../utils/formatters';
import { CLIENT_LABEL_MAPPING } from '../utils/clientLabels';
import { printQuotation } from '../utils/printQuotation';
import ProductSelectorModal from './ProductSelectorModal';
import './OrderModal.css';

const OrderModal = ({ order, clientId, client, onClose, onSave }) => {
    const [employees, setEmployees] = useState([]);
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);

    // Order State
    const [formData, setFormData] = useState({
        IDFuncionario: '',
        DataPedido: new Date().toISOString().split('T')[0],
        Frete: 0,
        AliquotaImposto: 0,
        VLEmpreiteira: 0,
        Entrada: 0,
        Quant: 1,
        OBS: '',
        ...order
    });

    const [items, setItems] = useState([]);
    const [photos, setPhotos] = useState([]);
    const [uploading, setUploading] = useState(false);
    const [selectedPhoto, setSelectedPhoto] = useState(null); // Para modal de visualização
    const [zoomLevel, setZoomLevel] = useState(1);
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
    const [originalFormData, setOriginalFormData] = useState(null);
    const [originalItems, setOriginalItems] = useState(null);
    const [autoSaveTimer, setAutoSaveTimer] = useState(null);
    const [searchTerms, setSearchTerms] = useState({}); // { rowIndex: 'search string' }
    const [isProductSelectorOpen, setIsProductSelectorOpen] = useState(false);
    const [activeRowIndex, setActiveRowIndex] = useState(null);

    // Image Compression Helper
    const compressImage = (file) => {
        return new Promise((resolve) => {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = (event) => {
                const img = new Image();
                img.src = event.target.result;
                img.onload = () => {
                    const canvas = document.createElement('canvas');
                    const MAX_WIDTH = 1200;
                    let width = img.width;
                    let height = img.height;

                    if (width > MAX_WIDTH) {
                        height *= MAX_WIDTH / width;
                        width = MAX_WIDTH;
                    }

                    canvas.width = width;
                    canvas.height = height;
                    const ctx = canvas.getContext('2d');
                    ctx.drawImage(img, 0, 0, width, height);

                    // Saída como Base64 (JPG para compressão melhor)
                    const dataUrl = canvas.toDataURL('image/jpeg', 0.7);
                    resolve(dataUrl);
                };
            };
        });
    };


    const formatPercent = (val) => {
        return formatNumber(val) + '%';
    };

    useEffect(() => {
        const fetchMetadata = async () => {
            try {
                const [empRes, prodRes] = await Promise.all([
                    fetch('/api/funcionarios', { credentials: 'include' }),
                    fetch('/api/produtos', { credentials: 'include' })
                ]);
                const empData = await empRes.json();
                const prodData = await prodRes.json();
                setEmployees(empData);
                setProducts(prodData);

                if (order && order.ID) {
                    const [detailsRes, photosRes] = await Promise.all([
                        fetch(`/api/pedidos/${order.ID}/detalhes`, { credentials: 'include' }),
                        fetch(`/api/pedidos/${order.ID}/fotos`, { credentials: 'include' })
                    ]);
                    const detailsData = await detailsRes.json();
                    const photosData = await photosRes.json();
                    setItems(Array.isArray(detailsData) ? detailsData : []);
                    setPhotos(Array.isArray(photosData) ? photosData : []);
                } else {
                    setItems([{ IDProduto: '', Quantidade: 0, PrecoUnitario: 0 }]);
                }
            } catch (error) {
                console.error('Erro ao carregar metadados:', error);
            } finally {
                setLoading(false);
            }
        };
        fetchMetadata();
    }, [order]);

    // Salvar estado original após carregamento
    useEffect(() => {
        if (!loading && formData && items.length > 0) {
            setOriginalFormData(JSON.parse(JSON.stringify(formData)));
            setOriginalItems(JSON.parse(JSON.stringify(items)));
        }
    }, [loading]);
    // Calculations
    const totalMaterial = items.reduce((sum, item) => sum + (Number(item.Quantidade) * Number(item.PrecoUnitario)), 0);
    const materialEFuncionario = totalMaterial + Number(formData.VLEmpreiteira);
    const impostoServico = Number(formData.Frete) * (Number(formData.AliquotaImposto) / 100);
    const lucroSemImposto = Number(formData.Frete) - materialEFuncionario;
    const lucroComImposto = lucroSemImposto - impostoServico;

    const valorEntrada = Number(formData.Frete) * (Number(formData.Entrada) / 100);
    const saldoRestante = Number(formData.Frete) - valorEntrada;
    const valorParcela = formData.Quant > 0 ? saldoRestante / formData.Quant : 0;

    const handleClose = () => {
        if (hasUnsavedChanges) {
            if (!window.confirm('Você tem alterações não salvas. Deseja sair sem salvar?')) {
                return;
            }
        }
        onClose();
    };

    const salvarAuto = useCallback(async () => {
        const payload = {
            ...formData,
            IDCliente: clientId,
            VLParcela: valorParcela,
            SaldoRestante: saldoRestante,
            VLEntrada: valorEntrada
        };

        try {
            let orderId = formData.ID || order?.ID;
            const method = orderId ? 'PUT' : 'POST';
            const url = orderId ? `/api/pedidos/${orderId}` : '/api/pedidos';

            const res = await fetch(url, {
                method,
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });
            const data = await res.json();

            if (res.ok) {
                const finalOrderId = orderId || data.ID;
                await fetch(`/api/pedidos/${finalOrderId}/detalhes`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ detalhes: items })
                });
                console.log('✅ Auto-save: Orçamento salvo automaticamente');
                setOriginalFormData(JSON.parse(JSON.stringify(formData)));
                setOriginalItems(JSON.parse(JSON.stringify(items)));
                setHasUnsavedChanges(false);
            }
        } catch (error) {
            console.error('Erro no auto-save do pedido:', error);
        }
    }, [formData, items, clientId, valorParcela, saldoRestante, valorEntrada, order]);

    // Detectar mudanças não salvas
    useEffect(() => {
        if (!originalFormData || !originalItems) {
            setHasUnsavedChanges(false);
            return;
        }

        const formChanged = JSON.stringify(formData) !== JSON.stringify(originalFormData);
        const itemsChanged = JSON.stringify(items) !== JSON.stringify(originalItems);
        const hasChanges = formChanged || itemsChanged;
        setHasUnsavedChanges(hasChanges);

        // Limpar timer anterior
        if (autoSaveTimer) {
            clearTimeout(autoSaveTimer);
        }

        // Se houver mudanças, iniciar timer de auto-save (1 minuto)
        if (hasChanges) {
            const timer = setTimeout(() => {
                salvarAuto();
            }, 60000); // 60 segundos
            setAutoSaveTimer(timer);
        }

        return () => {
            if (autoSaveTimer) {
                clearTimeout(autoSaveTimer);
            }
        };
    }, [formData, items, originalFormData, originalItems, salvarAuto]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData({ ...formData, [name]: value });
    };

    const handleItemChange = (index, field, value) => {
        const newItems = [...items];
        newItems[index][field] = value;

        if (field === 'IDProduto') {
            const product = products.find(p => String(p.ID) === String(value));
            if (product) {
                newItems[index].PrecoUnitario = product.PrecoUnitario;
            }
        }
        setItems(newItems);
    };

    const addItem = () => {
        setItems([...items, { IDProduto: '', Quantidade: 0, PrecoUnitario: 0 }]);
    };

    const removeItem = (index) => {
        setItems(items.filter((_, i) => i !== index));
        const newSearchTerms = { ...searchTerms };
        delete newSearchTerms[index];
        setSearchTerms(newSearchTerms);
    };

    const getFilteredProducts = (searchTerm) => {
        if (!searchTerm) return [];
        const terms = searchTerm.toLowerCase().split(' ').filter(t => t.length > 0);
        return products.filter(p => {
            const name = (p.NomeProduto || '').toLowerCase();
            return terms.every(term => name.includes(term));
        }).slice(0, 10); // Limit to 10 results for performance/UI
    };

    const handleKeyDown = (e, index, field) => {
        // Horizontal navigation
        if (e.key === 'ArrowLeft') {
            const inputs = e.target.closest('tr').querySelectorAll('input');
            const currentIndex = Array.from(inputs).indexOf(e.target);
            if (currentIndex > 0) {
                e.preventDefault();
                inputs[currentIndex - 1].focus();
            }
        } else if (e.key === 'ArrowRight') {
            const inputs = e.target.closest('tr').querySelectorAll('input');
            const currentIndex = Array.from(inputs).indexOf(e.target);
            if (currentIndex < inputs.length - 1) {
                e.preventDefault();
                inputs[currentIndex + 1].focus();
            }
        }

        // Vertical navigation
        if (e.key === 'ArrowUp') {
            e.preventDefault();
            const prevRow = e.target.closest('tr').previousElementSibling;
            if (prevRow) {
                const inputsInPrevRow = prevRow.querySelectorAll('input');
                const currentIndex = Array.from(e.target.closest('tr').querySelectorAll('input')).indexOf(e.target);
                if (inputsInPrevRow[currentIndex]) {
                    inputsInPrevRow[currentIndex].focus();
                } else if (inputsInPrevRow.length > 0) {
                    inputsInPrevRow[inputsInPrevRow.length - 1].focus();
                }
            }
        } else if (e.key === 'ArrowDown') {
            e.preventDefault();
            const nextRow = e.target.closest('tr').nextElementSibling;
            if (nextRow) {
                const inputsInNextRow = nextRow.querySelectorAll('input');
                const currentIndex = Array.from(e.target.closest('tr').querySelectorAll('input')).indexOf(e.target);
                if (inputsInNextRow[currentIndex]) {
                    inputsInNextRow[currentIndex].focus();
                } else if (inputsInNextRow.length > 0) {
                    inputsInNextRow[inputsInNextRow.length - 1].focus();
                }
            }
        }
    };

    const openProductSelector = (index) => {
        setActiveRowIndex(index);
        setIsProductSelectorOpen(true);
    };

    const handleProductSelect = (product) => {
        handleItemChange(activeRowIndex, 'IDProduto', product.ID);
        // Clear search term for this row
        const newSearchTerms = { ...searchTerms };
        delete newSearchTerms[activeRowIndex];
        setSearchTerms(newSearchTerms);
        setIsProductSelectorOpen(false);
    };

    const handleSave = async () => {
        const payload = {
            ...formData,
            IDCliente: clientId,
            VLParcela: valorParcela,
            SaldoRestante: saldoRestante,
            VLEntrada: valorEntrada
        };

        try {
            let orderId = formData.ID || order?.ID;
            const method = orderId ? 'PUT' : 'POST';
            const url = orderId ? `/api/pedidos/${orderId}` : '/api/pedidos';

            const res = await fetch(url, {
                method,
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });
            const data = await res.json();

            if (res.ok) {
                const finalOrderId = orderId || data.ID;
                // Save Details
                await fetch(`/api/pedidos/${finalOrderId}/detalhes`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ detalhes: items })
                });

                // Se for um novo pedido, precisamos atualizar o orderId localmente para permitir fotos sem fechar o modal
                if (!orderId) {
                    setFormData(prev => ({ ...prev, ID: finalOrderId }));
                }

                onSave();
                setHasUnsavedChanges(false);
                // No onClose if we want to add photos after save
                alert('Orçamento salvo com sucesso! Agora você pode adicionar fotos se desejar.');
            } else {
                alert('Erro ao salvar orçamento.');
            }
        } catch (error) {
            console.error('Erro ao salvar:', error);
        }
    };

    const handleAddPhoto = async (e) => {
        if (!formData.ID) {
            alert('Salve o orçamento primeiro para poder adicionar fotos.');
            return;
        }

        const files = Array.from(e.target.files);
        if (files.length === 0) return;

        const comentarioPadrão = window.prompt("Deseja adicionar um comentário/legenda para estas fotos? (Opcional)");

        setUploading(true);
        let successCount = 0;
        try {
            for (const file of files) {
                const compressed = await compressImage(file);
                const res = await fetch(`/api/pedidos/${formData.ID}/fotos`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        foto: compressed,
                        comentario: comentarioPadrão
                    }),
                    credentials: 'include'
                });

                if (res.ok) {
                    const newPhoto = await res.json();
                    setPhotos(prev => [...prev, {
                        ID: newPhoto.ID || newPhoto.id,
                        FotoBase64: compressed,
                        Comentario: comentarioPadrão
                    }]);
                    successCount++;
                }
            }
            if (successCount > 0) {
                alert(`${successCount} foto(s) enviada(s) com sucesso!`);
            }
        } catch (err) {
            console.error('Erro ao enviar fotos:', err);
            alert('Erro ao enviar fotos. Verifique o tamanho dos arquivos.');
        } finally {
            setUploading(false);
            e.target.value = ''; // Reset input
        }
    };

    const handleDeletePhoto = async (photoId) => {
        if (!window.confirm('Deseja remover esta foto?')) return;

        try {
            const res = await fetch(`/api/pedidos/fotos/${photoId}`, {
                method: 'DELETE',
                credentials: 'include'
            });
            if (res.ok) {
                setPhotos(prev => prev.filter(p => (p.ID || p.id) !== photoId));
            }
        } catch (err) {
            console.error('Erro ao remover foto:', err);
        }
    };

    const handleEditPhotoComment = async (photo) => {
        const newComentario = window.prompt("Editar legenda:", photo.Comentario || "");
        if (newComentario === null) return; // Cancelado

        try {
            const res = await fetch(`/api/pedidos/fotos/${photo.ID || photo.id}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ comentario: newComentario }),
                credentials: 'include'
            });
            if (res.ok) {
                setPhotos(prev => prev.map(p => (p.ID || p.id) === (photo.ID || photo.id) ? { ...p, Comentario: newComentario } : p));
                if ((selectedPhoto?.ID || selectedPhoto?.id) === (photo.ID || photo.id)) {
                    setSelectedPhoto({ ...selectedPhoto, Comentario: newComentario });
                }
            } else {
                alert('Erro ao atualizar legenda.');
            }
        } catch (err) {
            console.error('Erro ao editar legenda:', err);
        }
    };

    const handlePrint = () => {
        printQuotation(formData, items, client, products);
    };

    // No longer needed: print events handled by separate window pattern

    const handleZoomIn = (e) => {
        e.stopPropagation();
        setZoomLevel(prev => Math.min(prev + 0.5, 4));
    };

    const handleZoomOut = (e) => {
        e.stopPropagation();
        setZoomLevel(prev => Math.max(prev - 0.5, 0.5));
    };

    const resetZoom = (e) => {
        if (e) e.stopPropagation();
        setZoomLevel(1);
    };

    const closeLightbox = () => {
        setSelectedPhoto(null);
        setZoomLevel(1);
    };

    const longDate = new Date().toLocaleDateString('pt-BR', {
        weekday: 'long',
        day: 'numeric',
        month: 'long',
        year: 'numeric'
    });

    if (loading) {
        return (
            <div className="modal-overlay">
                <div className="modal-classic loading-container">
                    <img src="https://i.gifer.com/ZKZg.gif" alt="Carregando..." className="loading-img" />
                    <p>Carregando Dados do Orçamento...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="modal-overlay order-modal-overlay">
            <div className="modal-classic">
                <div className="modal-header">
                    <span className="title">Orçamentos</span>
                    <div className="window-controls">
                        <button onClick={handleClose}>_</button>
                        <button onClick={handleClose}>X</button>
                    </div>
                </div>

                <div className="modal-body">
                    <div className="top-section">
                        <div className="field employee-field">
                            <label>Funcionário</label>
                            <select name="IDFuncionario" value={formData.IDFuncionario} onChange={handleInputChange}>
                                <option value="">Selecione...</option>
                                {employees.map(emp => (
                                    <option key={emp.ID} value={emp.ID}>
                                        {emp.PrimeiroNome} {emp.Sobrenome}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div className="top-section-right">
                            <div className="field id-field">
                                <label>Número do Orçamento</label>
                                <input type="text" readOnly value={formData.ID || 'Novo'} />
                            </div>
                            <div className="field date-field">
                                <label>Data</label>
                                <input type="date" name="DataPedido" value={formData.DataPedido?.substring(0, 10)} onChange={handleInputChange} />
                            </div>
                        </div>
                    </div>

                    <div className="items-table-container">
                        <table className="items-table">
                            <thead>
                                <tr>
                                    <th>Produto</th>
                                    <th>Preço Unitário</th>
                                    <th>Quant.</th>
                                    <th>Total da Linha</th>
                                    <th></th>
                                </tr>
                            </thead>
                            <tbody>
                                {(items || []).map((item, index) => (
                                    <tr key={index}>
                                        <td className="product-cell">
                                            <div className="search-input-container">
                                                <input
                                                    type="text"
                                                    placeholder="Buscar produto..."
                                                    value={searchTerms[index] !== undefined ? searchTerms[index] : (products.find(p => String(p.ID) === String(item.IDProduto))?.NomeProduto || item.NomeProduto || '')}
                                                    onChange={(e) => setSearchTerms({ ...searchTerms, [index]: e.target.value })}
                                                    onFocus={() => {
                                                        if (!searchTerms[index] && !item.IDProduto) {
                                                            setSearchTerms({ ...searchTerms, [index]: '' });
                                                        }
                                                    }}
                                                    onKeyDown={(e) => handleKeyDown(e, index, 'IDProduto')}
                                                />
                                                <button className="btn-search-icon" onClick={() => openProductSelector(index)} title="Pesquisar Produto">🔍</button>

                                                {searchTerms[index] !== undefined && (
                                                    <div className="search-dropdown">
                                                        {getFilteredProducts(searchTerms[index]).length > 0 ? (
                                                            getFilteredProducts(searchTerms[index]).map(p => (
                                                                <div
                                                                    key={p.ID}
                                                                    className="dropdown-item"
                                                                    onClick={() => {
                                                                        handleItemChange(index, 'IDProduto', p.ID);
                                                                        const newSearch = { ...searchTerms };
                                                                        delete newSearch[index];
                                                                        setSearchTerms(newSearch);
                                                                    }}
                                                                >
                                                                    {p.NomeProduto} (ID: {p.ID})
                                                                </div>
                                                            ))
                                                        ) : (
                                                            <div className="dropdown-item no-results">Nenhum produto encontrado</div>
                                                        )}
                                                        <div className="dropdown-item close" onClick={() => {
                                                            const newSearch = { ...searchTerms };
                                                            delete newSearch[index];
                                                            setSearchTerms(newSearch);
                                                        }}>Fechar</div>
                                                    </div>
                                                )}
                                            </div>
                                            {item.NomeProduto && !item.IDProduto && !searchTerms[index] && (
                                                <div style={{ fontSize: '11px', color: '#000080', marginTop: '2px', fontWeight: 'bold' }}>
                                                    Item: {item.NomeProduto}
                                                </div>
                                            )}
                                        </td>
                                        <td className="price-cell">
                                            <input
                                                type="number"
                                                step="0.01"
                                                value={Number(item.PrecoUnitario || 0).toFixed(2)}
                                                onChange={(e) => handleItemChange(index, 'PrecoUnitario', e.target.value)}
                                                onKeyDown={(e) => handleKeyDown(e, index, 'PrecoUnitario')}
                                            />
                                        </td>
                                        <td className="quant-cell">
                                            <input
                                                type="number"
                                                value={item.Quantidade}
                                                onChange={(e) => handleItemChange(index, 'Quantidade', e.target.value)}
                                                onKeyDown={(e) => handleKeyDown(e, index, 'Quantidade')}
                                            />
                                        </td>
                                        <td className="total-cell">
                                            {formatCurrency(Number(item.Quantidade) * Number(item.PrecoUnitario))}
                                        </td>
                                        <td className="action-cell">
                                            <button className="btn-small" onClick={() => removeItem(index)}>x</button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        <button className="btn-classic" onClick={addItem}>+ Adicionar Linha</button>
                    </div>

                    <div className="calculations-grid">
                        <div className="calc-group left">
                            <div className="field-row highlight">
                                <label>Orçamento Valor</label>
                                <input type="number" step="0.01" name="Frete" value={Number(formData.Frete || 0).toFixed(2)} onChange={handleInputChange} />
                            </div>
                            <div className="field-row">
                                <label>Alíquota do ISS (%)</label>
                                <input type="number" name="AliquotaImposto" value={formData.AliquotaImposto} onChange={handleInputChange} />
                                <span className="calc-result">{formatCurrency(impostoServico)}</span>
                            </div>
                            <div className="field-row">
                                <label>Lucro s/ Imposto</label>
                                <span className="calc-result-box">{formatCurrency(lucroSemImposto)}</span>
                            </div>
                            <div className="field-row">
                                <label>Lucro c/ Imposto</label>
                                <span className="calc-result-box highlight-green">{formatCurrency(lucroComImposto)}</span>
                            </div>
                        </div>

                        <div className="calc-group right-top">
                            <div className="field-row">
                                <label>Material</label>
                                <span className="calc-result-box">{formatCurrency(totalMaterial)}</span>
                            </div>
                            <div className="field-row">
                                <label>Funcionario</label>
                                <input type="number" step="0.01" name="VLEmpreiteira" value={Number(formData.VLEmpreiteira || 0).toFixed(2)} onChange={handleInputChange} />
                            </div>
                            <div className="field-row">
                                <label>Material e Funcion</label>
                                <span className="calc-result-box">{formatCurrency(materialEFuncionario)}</span>
                            </div>
                        </div>

                        <div className="calc-group right-bottom">
                            <div className="field-row">
                                <label>Entrada %</label>
                                <input type="number" name="Entrada" value={formData.Entrada} onChange={handleInputChange} />
                                <span className="calc-result">{formatCurrency(valorEntrada)}</span>
                            </div>
                            <div className="field-row">
                                <label>Saldo Restante</label>
                                <span className="calc-result-box">{formatCurrency(saldoRestante)}</span>
                            </div>
                            <div className="field-row">
                                <label>Parcela (Qtd / Valor)</label>
                                <input type="number" name="Quant" value={formData.Quant} onChange={handleInputChange} className="small-input" />
                                <span className="calc-result-box">{formatCurrency(valorParcela)}</span>
                            </div>
                        </div>
                    </div>

                    <div className="obs-section">
                        <label>OBS.</label>
                        <textarea name="OBS" value={formData.OBS} onChange={handleInputChange} />
                    </div>

                    <div className="photos-section">
                        <div className="photos-header">
                            <label>Fotos do Orçamento (Local da Obra)</label>
                            <input
                                type="file"
                                multiple
                                accept="image/*"
                                id="budget-photos-input"
                                style={{ display: 'none' }}
                                onChange={handleAddPhoto}
                                disabled={uploading}
                            />
                            <button
                                className="btn-classic btn-sm"
                                onClick={() => document.getElementById('budget-photos-input').click()}
                                disabled={uploading || !formData.ID}
                            >
                                {uploading ? 'Enviando...' : '+ Adicionar Fotos'}
                            </button>
                        </div>

                        {!formData.ID && (
                            <p className="photo-hint">Salve o orçamento para habilitar o envio de fotos.</p>
                        )}

                        <div className="photos-grid">
                            {(photos || []).map(photo => (
                                <div key={photo.ID || photo.id} className="photo-card">
                                    <div className="photo-img-container" onClick={() => setSelectedPhoto(photo)}>
                                        <img src={photo.FotoBase64} alt="Budget" />
                                        {photo.Comentario && (
                                            <div className="photo-caption-overlay">
                                                {photo.Comentario}
                                            </div>
                                        )}
                                    </div>
                                    <div className="photo-actions">
                                        <button className="btn-edit-photo" onClick={() => handleEditPhotoComment(photo)} title="Editar Legenda">✎</button>
                                        <button className="btn-delete-photo" onClick={() => handleDeletePhoto(photo.ID || photo.id)} title="Remover Foto">×</button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                <div className="modal-footer">
                    <button className="btn-classic" onClick={handlePrint} style={{ backgroundColor: '#2196F3', color: 'white' }}>Imprimir</button>
                    <button className="btn-classic" onClick={handleSave}>Salvar</button>
                    <button className="btn-classic" onClick={handleClose}>Cancelar</button>
                    {hasUnsavedChanges && <span style={{ color: 'red', marginLeft: '10px' }}>* Alterações não salvas</span>}
                </div>

            </div>

            {/* Lightbox para visualização de fotos */}
            {selectedPhoto && (
                <div className="photo-lightbox" onClick={closeLightbox}>
                    <div className="lightbox-controls">
                        <button className="zoom-btn" onClick={handleZoomIn} title="Aumentar Zoom">+</button>
                        <button className="zoom-btn" onClick={handleZoomOut} title="Diminuir Zoom">−</button>
                        <button className="zoom-btn" onClick={resetZoom} title="Resetar Zoom">↺</button>
                        <button className="close-lightbox" onClick={closeLightbox}>×</button>
                    </div>
                    <div className="lightbox-content" onClick={e => e.stopPropagation()}>
                        <div className="lightbox-image-wrapper">
                            <img
                                src={selectedPhoto.FotoBase64}
                                alt="Full view"
                                style={{ transform: `scale(${zoomLevel})` }}
                            />
                        </div>
                        {selectedPhoto.Comentario && <div className="lightbox-caption">{selectedPhoto.Comentario}</div>}
                    </div>
                </div>
            )}

            {isProductSelectorOpen && (
                <ProductSelectorModal
                    onClose={() => setIsProductSelectorOpen(false)}
                    onSelect={handleProductSelect}
                />
            )}
        </div>
    );
};

export default OrderModal;
